// Reads a motor nameplate.
//
// This is the only implementation — scripts/test-attributes.mjs loads this same
// file, so what the tests prove is what the page runs.
//
// A nameplate is not a product title, and reading one with title parsers is
// unsafe. Titles write "15 HP"; plates write "HP 15". Run a real Baldor plate
// through a title parser and it returns 3 HP, because the spec number ends
// "...W595G3" and is followed by " HP 15". A 15 HP motor read as 3 HP is a motor
// that burns out on day one.
//
// So every field is anchored to its label and validated against the standard,
// and anything read by shape alone is returned as low confidence for a human to
// confirm rather than searched on silently.
(function (root) {
  'use strict';

  var ENCLOSURE_WORDS = {
    TEFC: 'TEFC',
    TENV: 'TENV',
    TEAO: 'TEAO',
    TEBC: 'TEBC',
    ODP: 'ODP',
    OPEN: 'ODP',
    DP: 'ODP',
    // Stamped plates abbreviate hard: "TE" is Totally Enclosed.
    TE: 'TEFC',
    WPI: 'WPI',
    WPII: 'WPII',
    XP: 'XP',
  };

  // Label first, then the same field written the other way round. The lookbehind
  // on the value-first forms is what stops "G3 HP" reading as 3 horsepower.
  var PLATE_FIELDS = {
    hp: [/\bHP[\s.:]*(\d+(?:\.\d+)?)\b/i, /(?<![A-Za-z0-9.])(\d+(?:\.\d+)?)\s*HP\b/i],
    rpm: [/\bR\.?\s?P\.?\s?M\.?[\s.:]*(\d{3,4})\b/i, /(?<![A-Za-z0-9.])(\d{3,4})\s*RPM\b/i],
    volts: [
      /\bVOLTS?[\s.:]*(\d{2,4}(?:\s*\/\s*\d{2,4})*)/i,
      /(?<![A-Za-z0-9.\/])(\d{3,4}(?:\s*\/\s*\d{3,4})+)(?![\d\/])/,
    ],
    frame: [/\bFRAME[\s.:]*(\d{2,3}(?:\/\d)?[A-Z]{0,3})\b/i, /(?<![A-Za-z0-9])(\d{2,3}T[SCYD]?)\b/i],
    phase: [/\bPH(?:ASE)?[\s.:]*([13])\b/i, /(?<![A-Za-z0-9.])([13])\s*(?:PH|PHASE)\b/i],
    amps: [/\bAMPS?[\s.:]*(\d+(?:\.\d+)?(?:\s*\/\s*\d+(?:\.\d+)?)*)/i],
    service_factor: [/\bS\.?E?R\.?\s*F\.?[\s.:]*(\d\.\d{1,2})\b/i],
    hz: [/\bHZ[\s.:]*(\d{2})\b/i, /(?<![A-Za-z0-9.])(\d{2})\s*HZ\b/i],
    catalog: [/\bCAT\.?\s*(?:NO\.?)?[\s.:#]*([A-Z0-9][A-Z0-9\-\/]{3,})/i],
    spec: [/\bSPEC\.?\s*(?:NO\.?)?[\s.:#]*([A-Z0-9][A-Z0-9\-\/]{5,})/i],
  };

  // The real NEMA frame numbers. Without this, "254T frame 230/460v" reads as
  // frame 230 — the label sits before the voltage, so anchoring to it is not
  // enough. 230 is not a frame; 254 is.
  var NEMA_FRAME_BASES = [
    '42', '48', '56', '66', '143', '145', '182', '184', '203', '204', '213',
    '215', '254', '256', '284', '286', '324', '326', '364', '365', '404', '405',
    '444', '445', '447', '449', '505', '507', '586', '587',
  ];

  function frameBase(value) {
    var match = String(value || '').match(/^\d{2,3}/);
    return match ? match[0] : '';
  }

  function isFrame(value) {
    return NEMA_FRAME_BASES.indexOf(frameBase(value)) !== -1;
  }

  // NEMA ties frame size to horsepower and speed, so a plate can be checked
  // rather than merely read.
  var NEMA_FRAMES = {
    '0.5|3600': '56', '0.5|1800': '56', '0.75|3600': '56', '0.75|1800': '56',
    '1|3600': '143T', '1|1800': '145T', '1.5|3600': '143T', '1.5|1800': '145T',
    '2|3600': '145T', '2|1800': '145T', '3|3600': '182T', '3|1800': '182T',
    '5|3600': '184T', '5|1800': '184T', '7.5|3600': '213T', '7.5|1800': '213T',
    '10|3600': '215T', '10|1800': '215T', '15|3600': '254T', '15|1800': '254T',
    '20|3600': '256T', '20|1800': '256T', '25|3600': '284TS', '25|1800': '284T',
    '30|3600': '286TS', '30|1800': '286T', '40|3600': '324TS', '40|1800': '324T',
    '50|3600': '326TS', '50|1800': '326T', '60|3600': '364TS', '60|1800': '364T',
    '75|3600': '365TS', '75|1800': '365T', '100|3600': '405TS', '100|1800': '404T',
  };

  function syncSpeed(rpm) {
    if (!rpm) return null;
    if (rpm >= 3000) return 3600;
    if (rpm >= 1500) return 1800;
    if (rpm >= 1000) return 1200;
    return null;
  }

  function expectedFrame(hp, rpm) {
    var speed = syncSpeed(rpm);
    if (!hp || !speed) return null;
    return NEMA_FRAMES[hp + '|' + speed] || null;
  }

  // The reverse lookup. Frames NEMA assigns to more than one rating at the same
  // speed return null rather than picking a side.
  function hpForFrame(frame, rpm) {
    var speed = syncSpeed(rpm);
    if (!speed || !frame) return null;
    var base = frameBase(frame);
    var found = [];
    for (var key in NEMA_FRAMES) {
      if (!Object.prototype.hasOwnProperty.call(NEMA_FRAMES, key)) continue;
      var parts = key.split('|');
      if (Number(parts[1]) !== speed) continue;
      if (frameBase(NEMA_FRAMES[key]) !== base) continue;
      var hp = Number(parts[0]);
      if (found.indexOf(hp) === -1) found.push(hp);
    }
    return found.length === 1 ? found[0] : null;
  }

  function firstMatch(text, patterns, accept) {
    for (var i = 0; i < patterns.length; i += 1) {
      var flags = patterns[i].flags.indexOf('g') === -1 ? patterns[i].flags + 'g' : patterns[i].flags;
      var scanner = new RegExp(patterns[i].source, flags);
      var match;
      // Scan every occurrence — a plate lists figures in whatever order the
      // manufacturer stamped them.
      while ((match = scanner.exec(text)) !== null) {
        var value = match[1].replace(/\s+/g, '');
        if (!accept || accept(value)) return { value: value, labelled: i === 0 };
      }
    }
    return null;
  }

  function parseNameplate(input) {
    var text = String(input || '').replace(/[–—]/g, '-');
    var found = {};
    var confidence = {};

    var accept = {
      frame: isFrame,
      rpm: function (v) { return Number(v) >= 300 && Number(v) <= 3700; },
      hz: function (v) { return Number(v) === 50 || Number(v) === 60; },
    };

    for (var field in PLATE_FIELDS) {
      if (!Object.prototype.hasOwnProperty.call(PLATE_FIELDS, field)) continue;
      var hit = firstMatch(text, PLATE_FIELDS[field], accept[field]);
      if (!hit) continue;
      found[field] = hit.value;
      confidence[field] = hit.labelled ? 'high' : 'medium';
    }

    var result = {
      hp: found.hp ? Number(found.hp) : null,
      rpm: found.rpm ? Number(found.rpm) : null,
      voltages: null,
      frame: found.frame ? found.frame.toUpperCase() : null,
      phase: found.phase ? Number(found.phase) : null,
      amps: found.amps || null,
      service_factor: found.service_factor ? Number(found.service_factor) : null,
      hz: found.hz ? Number(found.hz) : null,
      catalog: found.catalog ? found.catalog.toUpperCase() : null,
      spec: found.spec ? found.spec.toUpperCase() : null,
      enclosure: null,
      confidence: confidence,
    };

    if (found.volts) {
      var volts = [];
      var pieces = found.volts.split('/');
      for (var v = 0; v < pieces.length; v += 1) {
        var value = Number(pieces[v]);
        if (value >= 100 && value <= 5000) volts.push(value);
      }
      if (volts.length) result.voltages = volts;
    }

    var labelledEnclosure = text.match(/\bENCL\.?[\s.:]*([A-Z]{2,5})\b/i);
    if (labelledEnclosure && ENCLOSURE_WORDS[labelledEnclosure[1].toUpperCase()]) {
      result.enclosure = ENCLOSURE_WORDS[labelledEnclosure[1].toUpperCase()];
      confidence.enclosure = 'high';
    } else {
      var words = Object.keys(ENCLOSURE_WORDS).sort(function (a, b) { return b.length - a.length; });
      for (var w = 0; w < words.length; w += 1) {
        if (new RegExp('(?<![A-Za-z])' + words[w] + '(?![A-Za-z])', 'i').test(text)) {
          result.enclosure = ENCLOSURE_WORDS[words[w]];
          confidence.enclosure = words[w].length <= 2 ? 'medium' : 'high';
          break;
        }
      }
    }

    var labelledCount = 0;
    for (var key in confidence) {
      if (confidence[key] === 'high') labelledCount += 1;
    }
    result.unlabelled = labelledCount === 0;

    // On an unlabelled plate a bare four-figure number near a synchronous speed
    // is the speed. Nothing else on a plate lands in that range.
    if (result.rpm === null) {
      var bare = text.match(/(?<![A-Za-z0-9.\/])\d{3,4}(?![\d\/])/g) || [];
      var syncs = [3600, 1800, 1200, 900, 720];
      for (var b = 0; b < bare.length && result.rpm === null; b += 1) {
        for (var s = 0; s < syncs.length; s += 1) {
          if (Math.abs(Number(bare[b]) - syncs[s]) <= 200) {
            result.rpm = Number(bare[b]);
            confidence.rpm = 'low';
            break;
          }
        }
      }
    }

    // Horsepower is the one figure never worth guessing from a bare number. On a
    // stamped plate 15 is the rating, but 60 is the frequency and 38/19 the
    // current, and picking wrong sells someone a quarter of the motor they need.
    // NEMA ties frame to horsepower and speed, so derive it from those instead.
    if (result.hp === null && result.frame && result.rpm) {
      var derived = hpForFrame(result.frame, result.rpm);
      if (derived) {
        result.hp = derived;
        confidence.hp = 'low';
        result.hp_derived_from_frame = true;
      }
    }

    return result;
  }

  var api = {
    parseNameplate: parseNameplate,
    expectedFrame: expectedFrame,
    hpForFrame: hpForFrame,
    frameBase: frameBase,
  };

  root.NameplateParser = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
